import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Plus } from 'lucide-react';
import { subscribeTagAppearance } from '../../utils/tagAppearance';
import { TagBadge } from './TagBadge';
import { TagAppearancePanel } from './TagAppearancePanel';

type Props = {
  value: string;
  onChange: (v: string) => void;
  tags: string[];
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  allowCreate?: boolean;
  language?: string;
  showColorPicker?: boolean;
  onColorChange?: () => void;
};

export const TagCombobox: React.FC<Props> = ({
  value,
  onChange,
  tags,
  placeholder,
  required,
  invalid,
  className = '',
  allowCreate = true,
  language = 'ru-RU',
  showColorPicker = true,
  onColorChange,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const [colorTick, setColorTick] = useState(0);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => subscribeTagAppearance(() => setColorTick((tick) => tick + 1)), []);

  const placeMenu = useCallback(() => {
    const el = btnRef.current || rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 220) });
  }, []);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, placeMenu]);

  const tagList = Array.isArray(tags) ? tags : [];
  const q = query.trim();
  const qLower = q.toLowerCase();
  const filtered = qLower ? tagList.filter((t) => t.toLowerCase().includes(qLower)) : tagList;
  const exact = tagList.some((t) => t.toLowerCase() === qLower);
  const showCreate = allowCreate && q.length > 0 && !exact;
  const display = (value || '').trim() || placeholder || '—';
  const colorTag = (value || '').trim() || q;
  void colorTick;

  const commit = (v: string) => {
    const next = v.trim();
    onChange(next);
    setQuery(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        onClick={() => {
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className={`group w-full h-11 px-3.5 rounded-xl border text-left text-sm font-medium transition-all inline-flex items-center justify-between gap-2 ${
          invalid
            ? 'border-red-500 dark:border-red-400'
            : open
              ? 'border-blue-500 bg-white dark:bg-slate-800 ring-2 ring-blue-500/25 text-stone-900 dark:text-white shadow-sm'
              : 'border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-stone-900 dark:text-white hover:border-stone-300 dark:hover:border-slate-500 hover:bg-stone-50/80 dark:hover:bg-slate-800/80'
        }`}
      >
        <span className={`inline-flex min-w-0 flex-1 items-center gap-2 truncate ${value ? '' : 'text-stone-400 font-normal'}`}>
          {value ? <TagBadge tag={value} className="max-w-[min(100%,10rem)]" /> : null}
          {!value ? <span className="truncate">{display}</span> : null}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200 ${open ? 'rotate-180 text-blue-500' : 'group-hover:text-stone-600 dark:group-hover:text-slate-300'}`}
        />
      </button>
      <input type="hidden" value={value || ''} required={required} readOnly tabIndex={-1} aria-hidden />

      {open && menuPos && (
        <div
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, width: Math.max(menuPos.width, 260) }}
          className="z-[80] rounded-xl border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-xl shadow-stone-900/15 dark:shadow-black/50 overflow-hidden"
        >
          <div className="p-2 border-b border-stone-100 dark:border-slate-700">
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={placeholder}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (showCreate || exact) commit(query);
                  else if (filtered[0]) commit(filtered[0]);
                }
              }}
              className="w-full h-9 px-2.5 rounded-lg border border-stone-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500"
              autoComplete="off"
            />
          </div>
          <ul role="listbox" className="max-h-44 overflow-y-auto py-1 overscroll-contain">
            {filtered.map((t) => {
              const on = t === value;
              return (
                <li key={t} role="option" aria-selected={on}>
                  <button
                    type="button"
                    onClick={() => commit(t)}
                    className={`w-full px-3 py-2 text-left text-sm inline-flex items-center gap-2 transition-colors ${
                      on
                        ? 'bg-blue-50 text-blue-800 dark:bg-blue-950/50 dark:text-blue-100'
                        : 'text-stone-700 dark:text-slate-200 hover:bg-stone-50 dark:hover:bg-slate-700/80'
                    }`}
                  >
                    <TagBadge tag={t} className="max-w-[min(100%,12rem)] shrink-0" />
                    <span className="flex-1 min-w-0 truncate font-medium sr-only">{t}</span>
                    {on ? <Check className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-300" strokeWidth={3} /> : null}
                  </button>
                </li>
              );
            })}
            {showCreate && (
              <li role="option">
                <button
                  type="button"
                  onClick={() => commit(query)}
                  className="w-full px-3 py-2 text-left text-sm inline-flex items-center gap-2 text-sky-700 dark:text-sky-300 hover:bg-sky-50 dark:hover:bg-sky-950/40 font-medium"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{query.trim()}</span>
                </button>
              </li>
            )}
            {filtered.length === 0 && !showCreate && <li className="px-3 py-2 text-sm text-stone-400">—</li>}
          </ul>
          {showColorPicker && colorTag.trim() && (
            <div className="border-t border-stone-100 dark:border-slate-700 bg-stone-50/80 dark:bg-slate-900/40 max-h-72 overflow-y-auto">
              <TagAppearancePanel
                tag={colorTag}
                language={language}
                onAppearanceChange={() => {
                  setColorTick((tick) => tick + 1);
                  onColorChange?.();
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TagCombobox;
